/**
 * TranscriptManager - Manages voice transcripts with timestamps and session context
 * 
 * Handles transcript storage, session timeline integration, and privacy controls
 */

export class TranscriptManager {
    constructor(options = {}) {
        this.options = {
            // Storage settings
            maxTranscripts: options.maxTranscripts || 100,
            autoSave: options.autoSave !== false,
            storageKey: options.storageKey || 'chef_transcripts',
            
            // Privacy settings
            requireConfirmation: options.requireConfirmation !== false,
            autoDeleteAfter: options.autoDeleteAfter || 24 * 60 * 60 * 1000, // 24 hours
            
            // Session integration
            sessionId: options.sessionId || null,
            recipeId: options.recipeId || null,
            
            ...options
        };
        
        this.transcripts = [];
        this.pendingTranscripts = [];
        this.isEnabled = true;
        
        this.loadStoredTranscripts();
        this.setupAutoCleanup();
    }

    /**
     * Add a new transcript
     * @param {Object} transcriptData - Transcript data
     * @returns {string} Transcript ID
     */
    addTranscript(transcriptData) {
        const transcript = {
            id: `transcript_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            text: transcriptData.text,
            timestamp: transcriptData.timestamp || Date.now(),
            sessionId: transcriptData.sessionId || this.options.sessionId,
            recipeId: transcriptData.recipeId || this.options.recipeId,
            recipeName: transcriptData.recipeName || null,
            confidence: transcriptData.confidence || 0,
            isConfirmed: false,
            isProcessed: false,
            metadata: {
                engine: transcriptData.engine || 'unknown',
                duration: transcriptData.duration || 0,
                ...transcriptData.metadata
            }
        };

        if (this.options.requireConfirmation) {
            this.pendingTranscripts.push(transcript);
        } else {
            this.transcripts.push(transcript);
            if (this.options.autoSave) {
                this.saveTranscripts();
            }
        }

        return transcript.id;
    }

    /**
     * Confirm a pending transcript
     * @param {string} transcriptId - Transcript ID to confirm
     * @returns {boolean} Success status
     */
    confirmTranscript(transcriptId) {
        const index = this.pendingTranscripts.findIndex(t => t.id === transcriptId);
        if (index === -1) return false;

        const transcript = this.pendingTranscripts.splice(index, 1)[0];
        transcript.isConfirmed = true;
        transcript.confirmedAt = Date.now();
        
        this.transcripts.push(transcript);
        
        if (this.options.autoSave) {
            this.saveTranscripts();
        }

        return true;
    }

    /**
     * Reject a pending transcript
     * @param {string} transcriptId - Transcript ID to reject
     * @returns {boolean} Success status
     */
    rejectTranscript(transcriptId) {
        const index = this.pendingTranscripts.findIndex(t => t.id === transcriptId);
        if (index === -1) return false;

        this.pendingTranscripts.splice(index, 1);
        return true;
    }

    /**
     * Get transcripts for a session
     * @param {string} sessionId - Session ID
     * @returns {Array} Session transcripts
     */
    getSessionTranscripts(sessionId) {
        return this.transcripts.filter(t => t.sessionId === sessionId);
    }

    /**
     * Get transcripts for a recipe
     * @param {string} recipeId - Recipe ID
     * @returns {Array} Recipe transcripts
     */
    getRecipeTranscripts(recipeId) {
        return this.transcripts.filter(t => t.recipeId === recipeId);
    }

    /**
     * Get recent transcripts
     * @param {number} limit - Number of transcripts to return
     * @returns {Array} Recent transcripts
     */
    getRecentTranscripts(limit = 10) {
        return this.transcripts
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, limit);
    }

    /**
     * Get pending transcripts
     * @returns {Array} Pending transcripts
     */
    getPendingTranscripts() {
        return [...this.pendingTranscripts];
    }

    /**
     * Search transcripts
     * @param {string} query - Search query
     * @param {Object} options - Search options
     * @returns {Array} Matching transcripts
     */
    searchTranscripts(query, options = {}) {
        const {
            sessionId = null,
            recipeId = null,
            startDate = null,
            endDate = null,
            confirmedOnly = true
        } = options;

        let results = this.transcripts;

        // Filter by confirmation status
        if (confirmedOnly) {
            results = results.filter(t => t.isConfirmed);
        }

        // Filter by session
        if (sessionId) {
            results = results.filter(t => t.sessionId === sessionId);
        }

        // Filter by recipe
        if (recipeId) {
            results = results.filter(t => t.recipeId === recipeId);
        }

        // Filter by date range
        if (startDate) {
            results = results.filter(t => t.timestamp >= startDate);
        }
        if (endDate) {
            results = results.filter(t => t.timestamp <= endDate);
        }

        // Search in text
        if (query) {
            const searchQuery = query.toLowerCase();
            results = results.filter(t => 
                t.text.toLowerCase().includes(searchQuery)
            );
        }

        return results.sort((a, b) => b.timestamp - a.timestamp);
    }

    /**
     * Delete transcript
     * @param {string} transcriptId - Transcript ID to delete
     * @returns {boolean} Success status
     */
    deleteTranscript(transcriptId) {
        const index = this.transcripts.findIndex(t => t.id === transcriptId);
        if (index === -1) return false;

        this.transcripts.splice(index, 1);
        
        if (this.options.autoSave) {
            this.saveTranscripts();
        }

        return true;
    }

    /**
     * Clear all transcripts
     * @param {Object} options - Clear options
     */
    clearTranscripts(options = {}) {
        const { confirmedOnly = false, olderThan = null } = options;

        if (confirmedOnly) {
            this.transcripts = this.transcripts.filter(t => !t.isConfirmed);
        } else if (olderThan) {
            this.transcripts = this.transcripts.filter(t => t.timestamp >= olderThan);
        } else {
            this.transcripts = [];
        }

        if (this.options.autoSave) {
            this.saveTranscripts();
        }
    }

    /**
     * Export transcripts
     * @param {Object} options - Export options
     * @returns {Object} Exported data
     */
    exportTranscripts(options = {}) {
        const {
            format = 'json',
            sessionId = null,
            recipeId = null,
            includeMetadata = true
        } = options;

        let data = this.transcripts;

        // Filter data
        if (sessionId) {
            data = data.filter(t => t.sessionId === sessionId);
        }
        if (recipeId) {
            data = data.filter(t => t.recipeId === recipeId);
        }

        // Remove metadata if not requested
        if (!includeMetadata) {
            data = data.map(t => ({
                id: t.id,
                text: t.text,
                timestamp: t.timestamp,
                sessionId: t.sessionId,
                recipeId: t.recipeId
            }));
        }

        if (format === 'json') {
            return {
                transcripts: data,
                exportedAt: Date.now(),
                totalCount: data.length
            };
        } else if (format === 'csv') {
            return this.convertToCSV(data);
        }

        return data;
    }

    /**
     * Convert transcripts to CSV format
     * @param {Array} transcripts - Transcripts to convert
     * @returns {string} CSV data
     */
    convertToCSV(transcripts) {
        const headers = ['ID', 'Text', 'Timestamp', 'Session ID', 'Recipe ID', 'Recipe Name', 'Confidence'];
        const rows = transcripts.map(t => [
            t.id,
            `"${t.text.replace(/"/g, '""')}"`, // Escape quotes
            new Date(t.timestamp).toISOString(),
            t.sessionId || '',
            t.recipeId || '',
            t.recipeName || '',
            t.confidence
        ]);

        return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    }

    /**
     * Load stored transcripts from localStorage
     */
    loadStoredTranscripts() {
        try {
            const stored = localStorage.getItem(this.options.storageKey);
            if (stored) {
                const data = JSON.parse(stored);
                this.transcripts = data.transcripts || [];
                this.pendingTranscripts = data.pendingTranscripts || [];
                
                // Clean up old transcripts
                this.cleanupOldTranscripts();
            }
        } catch (error) {
            console.error('Failed to load stored transcripts:', error);
            this.transcripts = [];
            this.pendingTranscripts = [];
        }
    }

    /**
     * Save transcripts to localStorage
     */
    saveTranscripts() {
        try {
            const data = {
                transcripts: this.transcripts,
                pendingTranscripts: this.pendingTranscripts,
                savedAt: Date.now()
            };
            
            localStorage.setItem(this.options.storageKey, JSON.stringify(data));
        } catch (error) {
            console.error('Failed to save transcripts:', error);
        }
    }

    /**
     * Clean up old transcripts
     */
    cleanupOldTranscripts() {
        const cutoffTime = Date.now() - this.options.autoDeleteAfter;
        const initialCount = this.transcripts.length;
        
        this.transcripts = this.transcripts.filter(t => t.timestamp >= cutoffTime);
        
        const removedCount = initialCount - this.transcripts.length;
        if (removedCount > 0) {
            console.log(`Cleaned up ${removedCount} old transcripts`);
        }
    }

    /**
     * Setup automatic cleanup
     */
    setupAutoCleanup() {
        // Clean up every hour
        setInterval(() => {
            this.cleanupOldTranscripts();
            if (this.options.autoSave) {
                this.saveTranscripts();
            }
        }, 60 * 60 * 1000);
    }

    /**
     * Get transcript statistics
     * @returns {Object} Statistics
     */
    getStats() {
        const now = Date.now();
        const last24h = now - (24 * 60 * 60 * 1000);
        const last7d = now - (7 * 24 * 60 * 60 * 1000);

        return {
            total: this.transcripts.length,
            pending: this.pendingTranscripts.length,
            confirmed: this.transcripts.filter(t => t.isConfirmed).length,
            last24h: this.transcripts.filter(t => t.timestamp >= last24h).length,
            last7d: this.transcripts.filter(t => t.timestamp >= last7d).length,
            bySession: this.getSessionStats(),
            byRecipe: this.getRecipeStats()
        };
    }

    /**
     * Get session statistics
     * @returns {Object} Session stats
     */
    getSessionStats() {
        const sessionCounts = {};
        this.transcripts.forEach(t => {
            if (t.sessionId) {
                sessionCounts[t.sessionId] = (sessionCounts[t.sessionId] || 0) + 1;
            }
        });
        return sessionCounts;
    }

    /**
     * Get recipe statistics
     * @returns {Object} Recipe stats
     */
    getRecipeStats() {
        const recipeCounts = {};
        this.transcripts.forEach(t => {
            if (t.recipeId) {
                recipeCounts[t.recipeId] = (recipeCounts[t.recipeId] || 0) + 1;
            }
        });
        return recipeCounts;
    }

    /**
     * Update session context
     * @param {string} sessionId - Current session ID
     * @param {string} recipeId - Current recipe ID
     */
    updateContext(sessionId, recipeId) {
        this.options.sessionId = sessionId;
        this.options.recipeId = recipeId;
    }

    /**
     * Enable/disable transcript manager
     * @param {boolean} enabled - Enable status
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
    }

    /**
     * Check if transcript manager is enabled
     * @returns {boolean} Enabled status
     */
    isEnabled() {
        return this.isEnabled;
    }
}

